window.BAO.CarouselElement = class CarouselElement extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
    /* Required to ensure this.carousel is defined prior to initialisation */
    customElements.whenDefined(this.localName).then(this.initialise.bind(this))
  }

  initialise () {
    if (!this.carousel) return
    this.initialised = true
    this.afterCarouselConnected()
    this.setupAutoplay()
    this.setupCarouselListeners()
    this.setupMouseMoveHandler()
    this.setupWheelHandler()

    if (this.carousel.options.loop) {
      this.setAttribute(`data-has-loop`, true)
    }
  }

  reinitialise () {
    this.initialised = false
    this.initialise()
  }

  afterCarouselConnected () {
    this.dispatchEvent(
      new CustomEvent(`AFTER_CAROUSEL_CONNECTED`, {
        detail: {
          element: this,
        },
        bubbles: true,
      }),
    )
  }

  setupAutoplay () {
    if (!this.settings.autoplay || !this.carousel) return
    if (this.carousel?.track?.details?.slides?.length < 2) return
    this.addEventListener('mouseover', () => {
      this.autoplay(false, this.carousel)
    })
    this.addEventListener('mouseout', () => {
      this.autoplay(this.settings.autoplay, this.carousel)
    })
    this.autoplay(this.settings.autoplay, this.carousel)
  }

  setupCarouselListeners () {
    this.carousel.on('slideChanged', this.handleSlideChange.bind(this))
    this.carousel.on('optionsChanged', this.handleOptionsChanged.bind(this))
    this.carousel.on('dragged', this.handleDrag.bind(this))
    this.carousel.container.addEventListener(
      'onKeydown',
      this.handleKeyDown.bind(this),
    )
    this.carousel.container.setAttribute('tabindex', '0')
  }

  setupMouseMoveHandler () {
    this.carousel.container.addEventListener('mousemove', event => {
      this.cursorX = event.clientX
      this.cursorY = event.clientY
    })
  }

  /*
    Carousel currently doesn't support using a touchpad to scroll due to the touchpad scroll being a 'wheel' movement.
    Can be fixed by used the Keen scroll-wheel-controls below.

    https://keen-slider.io/examples#scroll-wheel-controls
    https://codesandbox.io/p/sandbox/github/rcbyr/keen-slider-sandboxes/tree/v6/navigation-controls/scroll-wheel-controls/javascript?file=%2Findex.html
  */

  setupWheelHandler () {
    this.listeners.add(
      this.carousel.container,
      'wheel',
      this.handleWheel.bind(this),
    )
  }

  handleWheel (event) {
    if (!this.carousel.options.drag) return
    if (!this.wheelActive && Math.abs(event.deltaX) <= Math.abs(event.deltaY)) {
      return
    }

    event.preventDefault()

    if (!this.wheelActive) {
      this.wheelActive = true
      this.wheelPosition = { x: event.pageX, y: event.pageY }
      this.dispatchWheelDrag(event, 'ksDragStart')
    }

    this.dispatchWheelDrag(event, 'ksDrag')

    clearTimeout(this.wheelTimeout)
    this.wheelTimeout = setTimeout(() => {
      this.wheelActive = false
      this.dispatchWheelDrag(event, 'ksDragEnd')
    }, 50)
  }

  dispatchWheelDrag (event, name) {
    this.wheelPosition.x -= event.deltaX
    this.wheelPosition.y -= event.deltaY
    this.carousel.container.dispatchEvent(
      new CustomEvent(name, {
        detail: {
          x: this.wheelPosition.x,
          y: this.wheelPosition.y,
        },
      }),
    )
  }

  handleSlideChange () {
    if (!this.carousel?.track?.details) return

    const loop = this.carousel.options.loop
    const index = this.carousel.track.details.rel
    this.currentSlide = index
    this.hasScrolled = loop || index > 0
    this.hasFinished = !loop && index >= this.maxIndex
    if (!this.hasFinished) {
      this.nextSlide = index + 1
    }
  }

  handleOptionsChanged () {}

  handleDrag () {
    document.dispatchEvent(
      new CustomEvent('UPDATE_CURSOR_SETTINGS', {
        detail: {
          cursorX: this.cursorX,
          cursorY: this.cursorY,
        },
      }),
    )
  }

  handleKeyDown (event) {
    if (!this.carousel) return

    switch (event.key) {
      case 'ArrowLeft':
        this.carousel.prev()
        break
      case 'ArrowRight':
        this.carousel.next()
        break
    }
  }

  set initialised (boolean) {
    this._initialised = boolean
    this.style.opacity = `1`
    this.style.visibility = `visible`
    this.setAttribute(`data-initialised`, boolean)
  }

  get initialised () {
    return this._initialised
  }

  set currentSlide (currentIndex) {
    this.carousel.slides.forEach(slideEl => {
      slideEl.setAttribute(
        'aria-current',
        slideEl?.dataset?.slide === currentIndex.toString(),
      )
    })
  }

  get currentSlide () {
    return this.carousel?.track?.details?.rel
  }

  set nextSlide (nextIndex) {
    const slideEls = this.carousel?.track?.details?.slides
    if (!slideEls) return
    const nextSlideIndex = slideEls[nextIndex] ? nextIndex : 0

    this.carousel.slides.forEach(slideEl => {
      const isNext =
        slideEl.dataset?.slide?.toString() === nextSlideIndex.toString()
      slideEl.setAttribute('data-next', isNext)
    })
  }

  get nextSlide () {
    return this.carousel?.track?.details?.slides.find(
      slideEl => slideEl.getAttribute('data-next') === 'true',
    )
  }

  set hasScrolled (currentIndex) {
    this._hasScrolled = currentIndex > 0
    this.setAttribute('data-has-scrolled', this._hasScrolled)
  }

  get hasScrolled () {
    return this._hasScrolled
  }

  set hasFinished (boolean) {
    this._hasFinished = boolean
    this.setAttribute('data-has-finished', this._hasFinished)
  }

  get hasFinished () {
    return this._hasFinished
  }

  get maxIndex () {
    const slideEls = this.carousel?.track?.details?.slides
    if (!slideEls) return 1
    return Math.ceil(slideEls.length - this.currentPerView)
  }

  get currentPerView () {
    return parseInt(this.carousel.options.slides.perView, 10)
  }

  get slideCount () {
    return this.carousel.slides.length
  }

  set settings (settings) {
    this._settings = settings
    this.setupAutoplay()
  }

  get settings () {
    if (this._settings) {
      return this._settings
    } else {
      return {
        autoplay: false,
        autoplayFrequencey: 5000,
      }
    }
  }

  autoplay (run) {
    clearInterval(this.interval)
    this.interval = setInterval(() => {
      if (run) {
        this.carousel.next()
      }
    }, this.settings?.autoplayFrequencey)
  }
}
